
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const API_URL = "https://acesso.cronosagenda.com.br";

const CORES = {
  fundo: "#f5f7fb",
  superficie: "#ffffff",
  texto: "#172033",
  secundario: "#778198",
  borda: "#e8edf5",
  verde: "#12a878",
  azul: "#4776e6",
  roxo: "#8b5cf6",
  laranja: "#f59e0b",
  vermelho: "#e5484d",
};

const PALETA_SERVICOS = [
  "#4776e6",
  "#12a878",
  "#8b5cf6",
  "#f59e0b",
  "#ec6b9b",
];

const moeda = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const numero = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 0,
});

const valorSeguro = (valor) => {
  const convertido = Number(valor);
  return Number.isFinite(convertido) ? convertido : 0;
};

const formatarMoeda = (valor) => moeda.format(valorSeguro(valor));

const formatarCompacto = (valor) => {
  const n = valorSeguro(valor);

  if (Math.abs(n) >= 1000000) {
    return `R$ ${(n / 1000000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} mi`;
  }

  if (Math.abs(n) >= 1000) {
    return `R$ ${(n / 1000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })} mil`;
  }

  return formatarMoeda(n);
};

const dataLocalISO = () => {
  const agora = new Date();
  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  const dia = String(agora.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

const dataValida = (valor) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(valor || "")) return false;

  const [ano, mes, dia] = valor.split("-").map(Number);
  const data = new Date(ano, mes - 1, dia);

  return (
    data.getFullYear() === ano &&
    data.getMonth() === mes - 1 &&
    data.getDate() === dia
  );
};

const erroDaResposta = (error, fallback) => {
  if (error?.response?.data instanceof Blob) {
    return fallback;
  }

  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    fallback
  );
};

const Icone = ({ nome, tamanho = 20 }) => {
  const props = {
    width: tamanho,
    height: tamanho,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const desenhos = {
    receita: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M3 10h18M7 15h3" />
      </>
    ),
    agenda: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M7 3v4M17 3v4M3 10h18M8 14h3M8 17h5" />
      </>
    ),
    cliente: (
      <>
        <circle cx="9" cy="8" r="4" />
        <path d="M2 21v-2a7 7 0 0 1 14 0v2M17 4a4 4 0 0 1 0 8M19 15a5 5 0 0 1 3 4v2" />
      </>
    ),
    grafico: (
      <>
        <path d="M3 3v18h18" />
        <path d="m7 14 4-4 4 3 6-7" />
      </>
    ),
    calendario: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M7 3v4M17 3v4M3 10h18" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12m-5-5 5 5 5-5" />
        <path d="M5 16v4h14v-4" />
      </>
    ),
    atualizar: (
      <>
        <path d="M20 7v5h-5M4 17v-5h5" />
        <path d="M5.6 9a7 7 0 0 1 11.8-2L20 12M4 12l2.6 5a7 7 0 0 0 11.8-2" />
      </>
    ),
    voltar: <path d="m15 18-6-6 6-6" />,
    limpar: (
      <>
        <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v6M14 10v6" />
      </>
    ),
    seta: <path d="m7 10 5 5 5-5" />,
    relogio: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    medalha: (
      <>
        <circle cx="12" cy="8" r="5" />
        <path d="m8 12-2 9 6-3 6 3-2-9" />
      </>
    ),
    escudo: (
      <>
        <path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
  };

  return <svg {...props}>{desenhos[nome] || desenhos.grafico}</svg>;
};

const Estilos = () => (
  <style>{`
    .ca-root {
      min-height: 100vh;
      background: ${CORES.fundo};
      color: ${CORES.texto};
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-size: 14px;
      line-height: 1.5;
      padding: 30px;
      box-sizing: border-box;
    }
    .ca-root *, .ca-root *::before, .ca-root *::after {
      box-sizing: border-box;
    }
    .ca-container { max-width: 1600px; margin: 0 auto; }
    .ca-topbar {
      display: flex; justify-content: space-between; align-items: center;
      gap: 20px; margin-bottom: 26px; flex-wrap: wrap;
    }
    .ca-heading { display: flex; align-items: center; gap: 14px; min-width: 0; }
    .ca-brand-icon {
      width: 48px; height: 48px; flex-shrink: 0; border-radius: 15px;
      display: grid; place-items: center; color: white;
      background: linear-gradient(145deg, #1b2943, #344d77);
      box-shadow: 0 8px 20px rgba(32, 52, 86, .16);
    }
    .ca-eyebrow {
      margin: 0 0 3px; color: ${CORES.secundario};
      font-size: 11px; font-weight: 800; letter-spacing: 1.4px;
      text-transform: uppercase;
    }
    .ca-title {
      margin: 0; font-size: clamp(24px, 2.5vw, 32px);
      letter-spacing: -1.1px; font-weight: 800; line-height: 1.2;
    }
    .ca-subtitle { color: ${CORES.secundario}; margin: 6px 0 0; font-size: 13px; }
    .ca-actions { display: flex; gap: 10px; flex-wrap: wrap; }
    .ca-btn {
      display: inline-flex; align-items: center; justify-content: center;
      gap: 8px; border: 1px solid transparent; border-radius: 11px;
      padding: 11px 15px; min-height: 42px; font: inherit;
      font-size: 13px; font-weight: 700; cursor: pointer;
      transition: transform .18s, box-shadow .18s, background .18s;
      white-space: nowrap;
    }
    .ca-btn:hover:not(:disabled) { transform: translateY(-1px); }
    .ca-btn:focus-visible, .ca-input:focus-visible {
      outline: 3px solid rgba(71, 118, 230, .25); outline-offset: 2px;
    }
    .ca-btn:disabled { cursor: not-allowed; opacity: .55; }
    .ca-btn-primary {
      background: #263b5e; color: white;
      box-shadow: 0 5px 12px rgba(38, 59, 94, .12);
    }
    .ca-btn-primary:hover:not(:disabled) { background: #344e78; }
    .ca-btn-secondary { background: white; border-color: ${CORES.borda}; color: #34405a; }
    .ca-btn-quiet { background: #f0f4fb; color: #4c5c78; }
    .ca-panel {
      background: ${CORES.superficie}; border: 1px solid ${CORES.borda};
      border-radius: 18px; box-shadow: 0 5px 22px rgba(30, 48, 80, .035);
      min-width: 0;
    }
    .ca-filter {
      display: flex; align-items: flex-end; justify-content: space-between;
      gap: 20px; padding: 21px 23px; margin-bottom: 22px; flex-wrap: wrap;
    }
    .ca-section-title { margin: 0; font-size: 15px; font-weight: 800; letter-spacing: -.2px; }
    .ca-section-caption { margin: 4px 0 0; color: ${CORES.secundario}; font-size: 12px; }
    .ca-filter-fields { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
    .ca-field { display: flex; flex-direction: column; gap: 6px; min-width: 145px; }
    .ca-label { color: #65718a; font-size: 11px; font-weight: 800; }
    .ca-input {
      width: 100%; min-height: 42px; border: 1px solid #dfe5ef;
      border-radius: 10px; padding: 10px 12px; color: ${CORES.texto};
      background: #fff; font: inherit; font-size: 13px;
    }
    .ca-filter-result {
      flex-basis: 100%; padding-top: 14px; border-top: 1px solid #edf0f6;
      color: ${CORES.secundario}; font-size: 12px;
    }
    .ca-filter-result strong { color: ${CORES.texto}; font-size: 15px; margin-left: 5px; }
    .ca-group-label {
      margin: 26px 0 12px; display: flex; align-items: center; gap: 9px;
      color: #66738b; font-size: 11px; font-weight: 800;
      letter-spacing: 1.1px; text-transform: uppercase;
    }
    .ca-group-label::after { content: ""; height: 1px; background: #e5eaf2; flex: 1; }
    .ca-metrics {
      display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 15px; margin-bottom: 6px;
    }
    .ca-metric { padding: 21px; position: relative; overflow: hidden; }
    .ca-metric-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .ca-metric-label { font-size: 12px; font-weight: 700; color: #69758b; }
    .ca-metric-icon {
      width: 39px; height: 39px; display: grid; place-items: center;
      border-radius: 12px; flex-shrink: 0;
    }
    .ca-metric-value {
      font-size: clamp(21px, 2.1vw, 29px); line-height: 1.25;
      font-weight: 800; letter-spacing: -1px; margin-top: 17px;
      overflow-wrap: anywhere;
    }
    .ca-metric-foot { color: #8791a4; font-size: 11px; margin-top: 8px; }
    .ca-chip {
      display: inline-flex; align-items: center; gap: 5px;
      border-radius: 8px; padding: 5px 8px; font-size: 10px;
      font-weight: 800; background: #f0f4fa; color: #64718a;
    }
    .ca-stat-grid {
      display: grid; grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 15px; margin-bottom: 22px;
    }
    .ca-stat { padding: 17px 19px; display: flex; gap: 12px; align-items: center; }
    .ca-stat-icon {
      width: 40px; height: 40px; flex-shrink: 0; display: grid;
      place-items: center; border-radius: 12px;
    }
    .ca-stat-label { color: #79849a; font-size: 11px; font-weight: 700; }
    .ca-stat-value { font-size: 20px; font-weight: 800; letter-spacing: -.5px; margin-top: 2px; }
    .ca-charts {
      display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 17px; margin-bottom: 17px;
    }
    .ca-chart-panel { padding: 22px; min-width: 0; }
    .ca-chart-head {
      display: flex; align-items: flex-start; justify-content: space-between;
      gap: 12px; margin-bottom: 22px;
    }
    .ca-chart-title { margin: 0; font-size: 14px; font-weight: 800; }
    .ca-chart-subtitle { margin: 5px 0 0; font-size: 11px; color: #8993a5; }
    .ca-chart-tag {
      padding: 5px 8px; border-radius: 8px; color: #5b6b88;
      background: #f3f6fb; font-size: 10px; font-weight: 800; white-space: nowrap;
    }
    .ca-full { margin-bottom: 17px; }
    .ca-service-grid {
      display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px;
    }
    .ca-service {
      padding: 16px; border: 1px solid #edf0f6; border-radius: 13px;
      background: linear-gradient(150deg, #fff, #fafbfe); min-width: 0;
    }
    .ca-service-top { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; }
    .ca-service-name { margin: 0; font-size: 12px; font-weight: 800; overflow-wrap: anywhere; }
    .ca-service-index {
      display: grid; place-items: center; width: 25px; height: 25px;
      border-radius: 8px; flex-shrink: 0; font-size: 11px; font-weight: 800;
    }
    .ca-service-value { font-size: 18px; font-weight: 800; letter-spacing: -.5px; margin-top: 18px; overflow-wrap: anywhere; }
    .ca-service-count { margin-top: 5px; color: #8490a5; font-size: 11px; }
    .ca-table-panel { overflow: hidden; margin-bottom: 22px; }
    .ca-table-head {
      display: flex; align-items: center; justify-content: space-between;
      gap: 15px; padding: 22px; flex-wrap: wrap;
    }
    .ca-table-count { background: #f1f4fa; color: #64718a; padding: 6px 10px; border-radius: 9px; font-size: 11px; font-weight: 800; }
    .ca-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .ca-table { width: 100%; border-collapse: collapse; text-align: left; }
    .ca-table th {
      background: #f8faff; padding: 13px 20px; color: #7b879c;
      border-top: 1px solid #edf0f6; border-bottom: 1px solid #edf0f6;
      font-size: 10px; text-transform: uppercase; letter-spacing: .65px;
      font-weight: 800; white-space: nowrap;
    }
    .ca-table td { padding: 15px 20px; border-bottom: 1px solid #f0f2f7; font-size: 12px; }
    .ca-table tbody tr:last-child td { border-bottom: 0; }
    .ca-table tbody tr:hover { background: #fafbfe; }
    .ca-client { display: flex; align-items: center; gap: 10px; min-width: 170px; }
    .ca-avatar {
      width: 34px; height: 34px; flex-shrink: 0; display: grid; place-items: center;
      border-radius: 11px; background: #edf2ff; color: #4776e6;
      font-size: 12px; font-weight: 800;
    }
    .ca-client-name { font-weight: 800; color: #27334a; }
    .ca-muted { color: #8791a4; }
    .ca-money { font-weight: 800; color: #11966c; white-space: nowrap; }
    .ca-count-pill { display: inline-flex; padding: 5px 9px; border-radius: 8px; background: #edf3ff; color: #4776e6; font-size: 11px; font-weight: 800; }
    .ca-empty { text-align: center; padding: 42px 20px; color: #8590a4; }
    .ca-empty-icon { margin: 0 auto 12px; width: 44px; height: 44px; display: grid; place-items: center; border-radius: 14px; background: #f1f4fa; color: #7b89a2; }
    .ca-alert {
      border: 1px solid #f3c9c9; border-radius: 12px; padding: 14px 16px;
      background: #fff4f4; color: #a43232; margin-bottom: 18px;
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
    }
    .ca-alert button { border: 0; background: transparent; color: inherit; cursor: pointer; font-weight: 800; }
    .ca-loading { min-height: 70vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 13px; color: #778198; }
    .ca-spinner { width: 30px; height: 30px; border: 3px solid #e2e8f3; border-top-color: #4776e6; border-radius: 50%; animation: ca-spin .75s linear infinite; }
    @keyframes ca-spin { to { transform: rotate(360deg); } }
    .ca-footer { display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap; padding: 0 2px 10px; color: #929caf; font-size: 11px; }
    .ca-dot { display: inline-block; width: 7px; height: 7px; background: #12a878; border-radius: 50%; margin-right: 6px; }
    .ca-tooltip { background: white; border: 1px solid #e8edf5; border-radius: 12px; padding: 12px 14px; box-shadow: 0 8px 30px rgba(25, 40, 70, .12); }
    .ca-tooltip-label { color: #7b879c; font-size: 11px; margin-bottom: 6px; }
    .ca-tooltip-value { color: #26344e; font-size: 13px; font-weight: 800; }
    @media (max-width: 1100px) {
      .ca-root { padding: 22px; }
      .ca-metrics, .ca-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .ca-service-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    }
    @media (max-width: 760px) {
      .ca-root { padding: 14px; }
      .ca-topbar { align-items: stretch; margin-bottom: 19px; }
      .ca-heading { align-items: flex-start; }
      .ca-brand-icon { width: 42px; height: 42px; border-radius: 13px; }
      .ca-title { font-size: 24px; }
      .ca-actions { display: grid; grid-template-columns: 1fr 1fr; }
      .ca-actions .ca-btn { width: 100%; padding: 10px 8px; white-space: normal; }
      .ca-filter { padding: 17px; align-items: stretch; }
      .ca-filter-fields { display: grid; grid-template-columns: 1fr 1fr; width: 100%; }
      .ca-field { min-width: 0; }
      .ca-filter-fields .ca-btn { width: 100%; padding: 10px 8px; }
      .ca-filter-fields .ca-field { grid-column: span 1; }
      .ca-metrics { gap: 10px; }
      .ca-metric { padding: 15px; border-radius: 14px; }
      .ca-metric-icon { width: 33px; height: 33px; border-radius: 10px; }
      .ca-metric-label { font-size: 11px; }
      .ca-metric-value { font-size: clamp(19px, 5vw, 25px); margin-top: 13px; }
      .ca-metric-foot { font-size: 10px; }
      .ca-stat-grid { gap: 10px; }
      .ca-stat { padding: 13px; gap: 9px; }
      .ca-stat-icon { width: 33px; height: 33px; border-radius: 10px; }
      .ca-stat-value { font-size: 18px; overflow-wrap: anywhere; }
      .ca-charts { grid-template-columns: minmax(0, 1fr); gap: 12px; }
      .ca-chart-panel { padding: 17px 12px; }
      .ca-chart-head { padding: 0 3px; margin-bottom: 15px; }
      .ca-service-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .ca-service { padding: 13px; }
      .ca-service-value { font-size: 16px; }
      .ca-table-head { padding: 17px; }
      .ca-table th, .ca-table td { padding: 12px; }
      .ca-footer { font-size: 10px; }
    }
    @media (max-width: 380px) {
      .ca-root { padding: 10px; }
      .ca-metric { padding: 12px; }
      .ca-metric-head { align-items: flex-start; }
      .ca-metric-icon { width: 29px; height: 29px; }
      .ca-filter-fields { grid-template-columns: 1fr; }
      .ca-filter-fields .ca-field { grid-column: auto; }
      .ca-service-grid { grid-template-columns: 1fr; }
    }
    @media (prefers-reduced-motion: reduce) {
      .ca-root *, .ca-root *::before, .ca-root *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; }
    }
  `}</style>
);

const PainelCard = ({ children, className = "", style = {} }) => (
  <section className={`ca-panel ${className}`} style={style}>
    {children}
  </section>
);

const CabecalhoGrafico = ({ titulo, subtitulo, tag }) => (
  <div className="ca-chart-head">
    <div>
      <h3 className="ca-chart-title">{titulo}</h3>
      <p className="ca-chart-subtitle">{subtitulo}</p>
    </div>
    {tag && <span className="ca-chart-tag">{tag}</span>}
  </div>
);

const TooltipFinanceiro = ({ active, payload, label, tipo = "moeda" }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="ca-tooltip">
      <div className="ca-tooltip-label">{label}</div>
      {payload.map((item, index) => {
        const ehMoeda =
          tipo === "moeda" ||
          item.dataKey === "faturamento";

        return (
          <div className="ca-tooltip-value" key={`${item.dataKey}-${index}`}>
            {item.name}:{" "}
            {ehMoeda
              ? formatarMoeda(item.value)
              : numero.format(valorSeguro(item.value))}
          </div>
        );
      })}
    </div>
  );
};

const DashboardAdmin = () => {
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    mensal: 0,
    semanal: 0,
    totalAgendamentos: 0,
    clienteMaisRecorrente: {
      nome: "",
      telefone: "",
      quantidade: 0,
    },
    listaClientes: [],
    ganhosPeriodo: 0,
    totalClientes: 0,
  });

  const [periodoFiltro, setPeriodoFiltro] = useState({
    inicio: "",
    fim: "",
  });

  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [dadosClientesGrafico, setDadosClientesGrafico] = useState([]);
  const [dadosMensalGrafico, setDadosMensalGrafico] = useState([]);
  const [servicosPopulares, setServicosPopulares] = useState([]);

  const [estatisticas, setEstatisticas] = useState({
    agendamentosHoje: 0,
    valorMedio: 0,
    novosClientesMes: 0,
  });

  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState("");
  const [avisoFiltro, setAvisoFiltro] = useState("");

  const intervaloValido = useMemo(() => {
    if (!periodoFiltro.inicio || !periodoFiltro.fim) return false;

    return (
      dataValida(periodoFiltro.inicio) &&
      dataValida(periodoFiltro.fim) &&
      periodoFiltro.inicio <= periodoFiltro.fim
    );
  }, [periodoFiltro]);

  const carregarDashboard = useCallback(
    async (inicio = null, fim = null, silencioso = false) => {
      if (silencioso) {
        setAtualizando(true);
      } else {
        setLoading(true);
      }

      setErro("");

      try {
        const response = await axios.get(`${API_URL}/api/dashboard`, {
          params: { inicio, fim },
          withCredentials: true,
        });

        const dados = response.data || {};
        const dashboard = dados.dashboard || {};

        setDashboardData((anterior) => ({
          ...anterior,
          ...dashboard,
          clienteMaisRecorrente: {
            ...anterior.clienteMaisRecorrente,
            ...(dashboard.clienteMaisRecorrente || {}),
          },
          listaClientes: Array.isArray(dashboard.listaClientes)
            ? dashboard.listaClientes
            : [],
          mensal: valorSeguro(dashboard.mensal),
          semanal: valorSeguro(dashboard.semanal),
          ganhosPeriodo: valorSeguro(dashboard.ganhosPeriodo),
          totalAgendamentos: valorSeguro(dashboard.totalAgendamentos),
          totalClientes: valorSeguro(dashboard.totalClientes),
        }));

        setDadosGrafico(Array.isArray(dados.grafico) ? dados.grafico : []);
        setDadosClientesGrafico(
          Array.isArray(dados.clientesGrafico) ? dados.clientesGrafico : []
        );
        setDadosMensalGrafico(
          Array.isArray(dados.mensalGrafico) ? dados.mensalGrafico : []
        );
        setServicosPopulares(
          Array.isArray(dados.servicosPopulares)
            ? dados.servicosPopulares
            : []
        );
      } catch (error) {
        console.error("Erro ao buscar dashboard:", error);

        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }

        setErro(
          erroDaResposta(error, "Não foi possível carregar os dados financeiros.")
        );
      } finally {
        setLoading(false);
        setAtualizando(false);
      }
    },
    [navigate]
  );

  const carregarEstatisticas = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/dashboard/estatisticas`,
        { withCredentials: true }
      );

      const dados = response.data || {};

      setEstatisticas({
        agendamentosHoje: valorSeguro(dados.agendamentosHoje),
        valorMedio: valorSeguro(dados.valorMedio),
        novosClientesMes: valorSeguro(dados.novosClientesMes),
      });
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);

      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }

      setErro((anterior) =>
        anterior ||
        "Não foi possível atualizar algumas estatísticas. Tente atualizar o painel."
      );
    }
  }, [navigate]);

  const carregarTudo = useCallback(
    async (inicio = null, fim = null, silencioso = false) => {
      await Promise.all([
        carregarDashboard(inicio, fim, silencioso),
        carregarEstatisticas(),
      ]);
    },
    [carregarDashboard, carregarEstatisticas]
  );

  useEffect(() => {
    let ativo = true;

    const inicializar = async () => {
      try {
        await axios.get(`${API_URL}/api/verificar-autenticacao`, {
          withCredentials: true,
        });

        if (ativo) {
          await carregarTudo();
        }
      } catch (error) {
        if (!ativo) return;

        console.error("Erro de autenticação:", error);
        navigate("/login");
      }
    };

    inicializar();

    return () => {
      ativo = false;
    };
  }, [carregarTudo, navigate]);

  const handleFiltrarPeriodo = async () => {
    setAvisoFiltro("");

    if (!periodoFiltro.inicio || !periodoFiltro.fim) {
      setAvisoFiltro("Selecione a data inicial e a data final.");
      return;
    }

    if (!dataValida(periodoFiltro.inicio) || !dataValida(periodoFiltro.fim)) {
      setAvisoFiltro("Informe datas válidas.");
      return;
    }

    if (periodoFiltro.inicio > periodoFiltro.fim) {
      setAvisoFiltro("A data inicial não pode ser posterior à data final.");
      return;
    }

    await carregarDashboard(periodoFiltro.inicio, periodoFiltro.fim);
  };

  const handleResetarFiltro = async () => {
    setPeriodoFiltro({ inicio: "", fim: "" });
    setAvisoFiltro("");
    await carregarDashboard();
  };

  const handleAtualizar = async () => {
    await carregarTudo(
      intervaloValido ? periodoFiltro.inicio : null,
      intervaloValido ? periodoFiltro.fim : null,
      true
    );
  };

  const handleExportarRelatorio = async () => {
    setAvisoFiltro("");

    if (!periodoFiltro.inicio || !periodoFiltro.fim) {
      setAvisoFiltro("Selecione um período antes de exportar o relatório.");
      return;
    }

    if (!intervaloValido) {
      setAvisoFiltro("Confira as datas selecionadas antes de exportar.");
      return;
    }

    setExportando(true);

    let urlTemporaria = null;

    try {
      const response = await axios.get(`${API_URL}/api/dashboard/exportar`, {
        params: {
          inicio: periodoFiltro.inicio,
          fim: periodoFiltro.fim,
        },
        withCredentials: true,
        responseType: "blob",
      });

      const blob = response.data;

      // O backend pode devolver um JSON de erro como Blob.
      if (blob.type?.includes("application/json")) {
        const texto = await blob.text();
        let mensagem = "Não foi possível exportar o relatório.";

        try {
          const json = JSON.parse(texto);
          mensagem = json.error || json.message || mensagem;
        } catch {
          // Mantém a mensagem padrão se a resposta não for JSON válido.
        }

        throw new Error(mensagem);
      }

      urlTemporaria = window.URL.createObjectURL(
        new Blob(["\uFEFF", blob], {
          type: "text/csv;charset=utf-8;",
        })
      );

      const link = document.createElement("a");
      link.href = urlTemporaria;
      link.download = `relatorio_${periodoFiltro.inicio}_${periodoFiltro.fim}.csv`;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        if (urlTemporaria) {
          window.URL.revokeObjectURL(urlTemporaria);
        }
      }, 1000);

      urlTemporaria = null;
    } catch (error) {
      console.error("Erro ao exportar relatório:", error);

      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }

      setAvisoFiltro(
        error.message || erroDaResposta(error, "Erro ao exportar o relatório.")
      );
    } finally {
      if (urlTemporaria) {
        window.URL.revokeObjectURL(urlTemporaria);
      }

      setExportando(false);
    }
  };

  const clienteRecorrente = dashboardData.clienteMaisRecorrente || {};
  const clientes = dashboardData.listaClientes || [];

  if (loading) {
    return (
      <>
        <Estilos />
        <main className="ca-root">
          <div className="ca-loading" role="status" aria-live="polite">
            <div className="ca-spinner" />
            <strong style={{ color: CORES.texto, fontSize: 16 }}>
              Preparando seu painel financeiro
            </strong>
            <span>Estamos carregando seus indicadores.</span>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Estilos />

      <main className="ca-root">
        <div className="ca-container">
          <header className="ca-topbar">
            <div className="ca-heading">
              <div className="ca-brand-icon">
                <Icone nome="grafico" tamanho={25} />
              </div>

              <div>
                <p className="ca-eyebrow">Cronos Agenda · Gestão</p>
                <h1 className="ca-title">Painel financeiro</h1>
                <p className="ca-subtitle">
                  Acompanhe o desempenho e os indicadores do seu negócio.
                </p>
              </div>
            </div>

            <div className="ca-actions">
              <button
                type="button"
                className="ca-btn ca-btn-secondary"
                onClick={() => navigate("/dashboard")}
              >
                <Icone nome="voltar" tamanho={17} />
                Voltar ao painel
              </button>

              <button
                type="button"
                className="ca-btn ca-btn-primary"
                onClick={handleAtualizar}
                disabled={atualizando}
              >
                <Icone nome="atualizar" tamanho={16} />
                {atualizando ? "Atualizando..." : "Atualizar dados"}
              </button>
            </div>
          </header>

          {erro && (
            <div className="ca-alert" role="alert">
              <span>{erro}</span>
              <button type="button" onClick={() => setErro("")}>
                Fechar
              </button>
            </div>
          )}

          <PainelCard className="ca-filter">
            <div>
              <h2 className="ca-section-title">Relatório por período</h2>
              <p className="ca-section-caption">
                Consulte os ganhos e exporte os agendamentos concluídos.
              </p>
            </div>

            <div className="ca-filter-fields">
              <div className="ca-field">
                <label className="ca-label" htmlFor="ca-inicio">
                  Data inicial
                </label>
                <input
                  id="ca-inicio"
                  className="ca-input"
                  type="date"
                  max={periodoFiltro.fim || undefined}
                  value={periodoFiltro.inicio}
                  onChange={(event) => {
                    setPeriodoFiltro((anterior) => ({
                      ...anterior,
                      inicio: event.target.value,
                    }));
                    setAvisoFiltro("");
                  }}
                />
              </div>

              <div className="ca-field">
                <label className="ca-label" htmlFor="ca-fim">
                  Data final
                </label>
                <input
                  id="ca-fim"
                  className="ca-input"
                  type="date"
                  min={periodoFiltro.inicio || undefined}
                  max={dataLocalISO()}
                  value={periodoFiltro.fim}
                  onChange={(event) => {
                    setPeriodoFiltro((anterior) => ({
                      ...anterior,
                      fim: event.target.value,
                    }));
                    setAvisoFiltro("");
                  }}
                />
              </div>

              <button
                type="button"
                className="ca-btn ca-btn-primary"
                onClick={handleFiltrarPeriodo}
                disabled={atualizando}
              >
                <Icone nome="calendario" tamanho={16} />
                Aplicar filtro
              </button>

              <button
                type="button"
                className="ca-btn ca-btn-secondary"
                onClick={handleResetarFiltro}
                disabled={atualizando}
              >
                <Icone nome="limpar" tamanho={16} />
                Limpar
              </button>

              <button
                type="button"
                className="ca-btn ca-btn-quiet"
                onClick={handleExportarRelatorio}
                disabled={!intervaloValido || exportando}
              >
                <Icone nome="download" tamanho={16} />
                {exportando ? "Exportando..." : "Exportar CSV"}
              </button>
            </div>

            {avisoFiltro && (
              <div
                className="ca-filter-result"
                style={{ color: CORES.vermelho }}
                role="alert"
              >
                {avisoFiltro}
              </div>
            )}

            {periodoFiltro.inicio && periodoFiltro.fim && intervaloValido && (
              <div className="ca-filter-result">
                Ganhos registrados no período selecionado:
                <strong>{formatarMoeda(dashboardData.ganhosPeriodo)}</strong>
              </div>
            )}
          </PainelCard>

          <div className="ca-group-label">Visão geral financeira</div>

          <section className="ca-metrics" aria-label="Indicadores financeiros">
            <PainelCard className="ca-metric">
              <div className="ca-metric-head">
                <span className="ca-metric-label">Faturamento mensal</span>
                <span
                  className="ca-metric-icon"
                  style={{ background: "#e5f8f0", color: CORES.verde }}
                >
                  <Icone nome="receita" />
                </span>
              </div>
              <div className="ca-metric-value">
                {formatarCompacto(dashboardData.mensal)}
              </div>
              <div className="ca-metric-foot">
                Total registrado no mês atual
              </div>
            </PainelCard>

            <PainelCard className="ca-metric">
              <div className="ca-metric-head">
                <span className="ca-metric-label">Faturamento semanal</span>
                <span
                  className="ca-metric-icon"
                  style={{ background: "#eaf0ff", color: CORES.azul }}
                >
                  <Icone nome="grafico" />
                </span>
              </div>
              <div className="ca-metric-value">
                {formatarCompacto(dashboardData.semanal)}
              </div>
              <div className="ca-metric-foot">
                Total registrado na semana atual
              </div>
            </PainelCard>

            <PainelCard className="ca-metric">
              <div className="ca-metric-head">
                <span className="ca-metric-label">Agendamentos concluídos</span>
                <span
                  className="ca-metric-icon"
                  style={{ background: "#fff3dc", color: CORES.laranja }}
                >
                  <Icone nome="agenda" />
                </span>
              </div>
              <div className="ca-metric-value">
                {numero.format(valorSeguro(dashboardData.totalAgendamentos))}
              </div>
              <div className="ca-metric-foot">
                Quantidade registrada no painel
              </div>
            </PainelCard>

            <PainelCard className="ca-metric">
              <div className="ca-metric-head">
                <span className="ca-metric-label">Cliente mais recorrente</span>
                <span
                  className="ca-metric-icon"
                  style={{ background: "#f0eaff", color: CORES.roxo }}
                >
                  <Icone nome="medalha" />
                </span>
              </div>
              <div
                className="ca-metric-value"
                title={clienteRecorrente.nome || ""}
                style={{ fontSize: 19 }}
              >
                {clienteRecorrente.nome || "Nenhum cliente"}
              </div>
              <div className="ca-metric-foot">
                {numero.format(valorSeguro(clienteRecorrente.quantidade))}{" "}
                agendamentos registrados
              </div>
            </PainelCard>
          </section>

          <div className="ca-group-label">Indicadores operacionais</div>

          <section className="ca-stat-grid" aria-label="Estatísticas operacionais">
            <PainelCard className="ca-stat">
              <span
                className="ca-stat-icon"
                style={{ background: "#eaf0ff", color: CORES.azul }}
              >
                <Icone nome="calendario" />
              </span>
              <div>
                <div className="ca-stat-label">Concluídos hoje</div>
                <div className="ca-stat-value">
                  {numero.format(valorSeguro(estatisticas.agendamentosHoje))}
                </div>
              </div>
            </PainelCard>

            <PainelCard className="ca-stat">
              <span
                className="ca-stat-icon"
                style={{ background: "#e5f8f0", color: CORES.verde }}
              >
                <Icone nome="receita" />
              </span>
              <div>
                <div className="ca-stat-label">Ticket médio</div>
                <div className="ca-stat-value">
                  {formatarCompacto(estatisticas.valorMedio)}
                </div>
              </div>
            </PainelCard>

            <PainelCard className="ca-stat">
              <span
                className="ca-stat-icon"
                style={{ background: "#fff3dc", color: CORES.laranja }}
              >
                <Icone nome="cliente" />
              </span>
              <div>
                <div className="ca-stat-label">Novos clientes</div>
                <div className="ca-stat-value">
                  {numero.format(valorSeguro(estatisticas.novosClientesMes))}
                </div>
                <div className="ca-muted" style={{ fontSize: 10 }}>
                  Neste mês
                </div>
              </div>
            </PainelCard>

            <PainelCard className="ca-stat">
              <span
                className="ca-stat-icon"
                style={{ background: "#f0eaff", color: CORES.roxo }}
              >
                <Icone nome="cliente" />
              </span>
              <div>
                <div className="ca-stat-label">Total de clientes</div>
                <div className="ca-stat-value">
                  {numero.format(valorSeguro(dashboardData.totalClientes))}
                </div>
                <div className="ca-muted" style={{ fontSize: 10 }}>
                  Na base do painel
                </div>
              </div>
            </PainelCard>
          </section>

          <div className="ca-group-label">Análise de desempenho</div>

          <section className="ca-charts">
            <PainelCard className="ca-chart-panel">
              <CabecalhoGrafico
                titulo="Evolução do faturamento"
                subtitulo="Receita registrada nos últimos sete dias"
                tag="7 DIAS"
              />

              {dadosGrafico.length > 0 ? (
                <div style={{ width: "100%", height: 290 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={dadosGrafico}
                      margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        stroke="#edf0f6"
                        strokeDasharray="4 5"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="data"
                        tick={{ fill: "#8791a4", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={10}
                        minTickGap={10}
                      />
                      <YAxis
                        tick={{ fill: "#8791a4", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) =>
                          valorSeguro(value) >= 1000
                            ? `R$ ${(value / 1000).toLocaleString("pt-BR", {
                                maximumFractionDigits: 0,
                              })} mil`
                            : `R$ ${numero.format(valorSeguro(value))}`
                        }
                        width={72}
                      />
                      <Tooltip
                        content={<TooltipFinanceiro tipo="moeda" />}
                        cursor={{ stroke: "#dfe5ef", strokeDasharray: "4 4" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="faturamento"
                        name="Faturamento"
                        stroke={CORES.verde}
                        strokeWidth={3}
                        activeDot={{ r: 6, strokeWidth: 0 }}
                        dot={{ r: 3, fill: CORES.verde, strokeWidth: 0 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="ca-empty">Sem dados de faturamento para exibir.</div>
              )}
            </PainelCard>

            <PainelCard className="ca-chart-panel">
              <CabecalhoGrafico
                titulo="Clientes mais frequentes"
                subtitulo="Ranking por quantidade de agendamentos"
                tag="TOP 5"
              />

              {dadosClientesGrafico.length > 0 ? (
                <div style={{ width: "100%", height: 290 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dadosClientesGrafico.slice(0, 5)}
                      margin={{ top: 8, right: 8, left: -15, bottom: 0 }}
                    >
                      <CartesianGrid
                        stroke="#edf0f6"
                        strokeDasharray="4 5"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="nome"
                        tick={{ fill: "#8791a4", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={10}
                        interval={0}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: "#8791a4", fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        width={35}
                      />
                      <Tooltip
                        content={<TooltipFinanceiro tipo="numero" />}
                        cursor={{ fill: "#f4f6fb" }}
                      />
                      <Bar
                        dataKey="quantidade"
                        name="Agendamentos"
                        fill={CORES.azul}
                        radius={[6, 6, 0, 0]}
                        maxBarSize={44}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="ca-empty">Sem dados de clientes para exibir.</div>
              )}
            </PainelCard>
          </section>

          {dadosMensalGrafico.length > 0 && (
            <PainelCard className="ca-chart-panel ca-full">
              <CabecalhoGrafico
                titulo="Desempenho mensal"
                subtitulo="Comparativo de agendamentos e faturamento"
                tag="6 MESES"
              />

              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dadosMensalGrafico}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      stroke="#edf0f6"
                      strokeDasharray="4 5"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="mes"
                      tick={{ fill: "#8791a4", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickMargin={10}
                    />
                    <YAxis
                      yAxisId="agendamentos"
                      allowDecimals={false}
                      tick={{ fill: "#8791a4", fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={35}
                    />
                    <YAxis
                      yAxisId="faturamento"
                      orientation="right"
                      tick={{ fill: "#8791a4", fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                      width={80}
                      tickFormatter={(value) =>
                        valorSeguro(value) >= 1000
                          ? `R$ ${(value / 1000).toLocaleString("pt-BR", {
                              maximumFractionDigits: 0,
                            })} mil`
                          : `R$ ${numero.format(valorSeguro(value))}`
                      }
                    />
                    <Tooltip
                      content={<TooltipFinanceiro />}
                      labelFormatter={(label) => `Mês: ${label}`}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, paddingTop: 15 }}
                    />
                    <Bar
                      yAxisId="agendamentos"
                      dataKey="agendamentos"
                      name="Agendamentos"
                      fill={CORES.azul}
                      radius={[5, 5, 0, 0]}
                      maxBarSize={35}
                    />
                    <Line
                      yAxisId="faturamento"
                      type="monotone"
                      dataKey="faturamento"
                      name="Faturamento"
                      stroke={CORES.verde}
                      strokeWidth={3}
                      activeDot={{ r: 5 }}
                      dot={{ r: 3, fill: CORES.verde }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </PainelCard>
          )}

          {servicosPopulares.length > 0 && (
            <>
              <div className="ca-group-label">Desempenho por serviço</div>

              <PainelCard className="ca-chart-panel ca-full">
                <CabecalhoGrafico
                  titulo="Serviços mais populares"
                  subtitulo="Serviços com mais atendimentos nos últimos 30 dias"
                  tag={`${servicosPopulares.length} SERVIÇOS`}
                />

                <div className="ca-service-grid">
                  {servicosPopulares.map((servico, index) => {
                    const quantidade = valorSeguro(servico.quantidade);
                    const faturamento = valorSeguro(servico.faturamento);
                    const cor = PALETA_SERVICOS[index % PALETA_SERVICOS.length];

                    return (
                      <article className="ca-service" key={`${servico.nome}-${index}`}>
                        <div className="ca-service-top">
                          <h3 className="ca-service-name">
                            {servico.nome || "Serviço"}
                          </h3>
                          <span
                            className="ca-service-index"
                            style={{
                              color: cor,
                              background: `${cor}18`,
                            }}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </div>

                        <div className="ca-service-value">
                          {formatarMoeda(faturamento)}
                        </div>

                        <div className="ca-service-count">
                          {numero.format(quantidade)}{" "}
                          {quantidade === 1 ? "atendimento" : "atendimentos"}
                        </div>

                        <div
                          style={{
                            height: 4,
                            background: "#edf0f6",
                            borderRadius: 99,
                            marginTop: 13,
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${
                                quantidade > 0
                                  ? Math.max(
                                      5,
                                      (quantidade /
                                        Math.max(
                                          1,
                                          ...servicosPopulares.map((item) =>
                                            valorSeguro(item.quantidade)
                                          )
                                        )) *
                                        100
                                    )
                                  : 0
                              }%`,
                              height: "100%",
                              borderRadius: 99,
                              background: cor,
                            }}
                          />
                        </div>

                        <div className="ca-service-count" style={{ marginTop: 9 }}>
                          Média por atendimento:{" "}
                          <strong style={{ color: CORES.texto }}>
                            {formatarMoeda(
                              quantidade > 0 ? faturamento / quantidade : 0
                            )}
                          </strong>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </PainelCard>
            </>
          )}

          <div className="ca-group-label">Relacionamento com clientes</div>

          <PainelCard className="ca-table-panel">
            <div className="ca-table-head">
              <div>
                <h2 className="ca-section-title">Base de clientes</h2>
                <p className="ca-section-caption">
                  Histórico consolidado de recorrência e valores registrados.
                </p>
              </div>

              <span className="ca-table-count">
                {numero.format(clientes.length)}{" "}
                {clientes.length === 1 ? "cliente" : "clientes"}
              </span>
            </div>

            {clientes.length > 0 ? (
              <div className="ca-table-scroll">
                <table className="ca-table">
                  <thead>
                    <tr>
                      <th scope="col">Cliente</th>
                      <th scope="col">Telefone</th>
                      <th scope="col">Total gasto</th>
                      <th scope="col">Agendamentos</th>
                    </tr>
                  </thead>

                  <tbody>
                    {clientes.map((cliente, index) => {
                      const nome = cliente.nome || "Cliente sem nome";
                      const inicial = nome.trim().charAt(0).toUpperCase() || "?";

                      return (
                        <tr key={`${cliente.telefone || nome}-${index}`}>
                          <td>
                            <div className="ca-client">
                              <span className="ca-avatar">{inicial}</span>
                              <span className="ca-client-name">{nome}</span>
                            </div>
                          </td>

                          <td className="ca-muted">
                            {cliente.telefone || "—"}
                          </td>

                          <td className="ca-money">
                            {formatarMoeda(cliente.totalGasto)}
                          </td>

                          <td>
                            <span className="ca-count-pill">
                              {numero.format(
                                valorSeguro(cliente.quantidadeAgendamentos)
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ca-empty">
                <div className="ca-empty-icon">
                  <Icone nome="cliente" tamanho={22} />
                </div>
                <strong style={{ color: CORES.texto }}>
                  Nenhum cliente encontrado
                </strong>
                <p style={{ margin: "6px 0 0", fontSize: 12 }}>
                  Os clientes aparecerão aqui quando houver dados disponíveis.
                </p>
              </div>
            )}
          </PainelCard>

          <footer className="ca-footer">
            <span>
              <span className="ca-dot" />
              Cronos Agenda · Painel financeiro
            </span>
            <span>
              Os indicadores refletem os dados disponibilizados pelo sistema.
            </span>
          </footer>
        </div>
      </main>
    </>
  );
};

export default DashboardAdmin;
